import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, Quote, ArrowRight } from "lucide-react";
import { useI18n, pick } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import Flag from "@/components/Flag";

const initialsAvatar = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "?")}&background=2563EB&color=fff&size=128&bold=true`;

export default function ReviewsTeaser() {
  const { lang } = useI18n();
  const { content, reviews } = useContent();
  const sec = content?.reviews_section;
  if (!sec) return null;
  const h = pick(sec, lang, sec.ru);
  const preview = reviews.slice(0, 3);

  return (
    <section id="reviews" className="py-16 sm:py-20 lg:py-32" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-end justify-between gap-5 sm:gap-6 mb-10 sm:mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-3 sm:mb-4">
              {lang === "ru" ? "Отзывы" : "Reviews"}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-tight">{h.title}</h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-muted-foreground">{h.subtitle}</p>
          </div>
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-foreground text-background font-semibold text-sm hover:translate-y-[-2px] transition-transform"
            data-testid="see-all-reviews-btn"
          >
            {lang === "ru" ? "Смотреть все отзывы" : "See all reviews"}
            <ArrowRight className="size-4" />
          </Link>
        </div>

        {preview.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            {lang === "ru" ? "Пока нет отзывов." : "No reviews yet."}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {preview.map((r, i) => (
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative p-5 sm:p-7 lg:p-8 rounded-3xl border border-border bg-card hover:-translate-y-1 transition-transform duration-300 will-change-transform"
                data-testid={`review-preview-${i}`}
              >
                <Quote className="absolute top-6 right-6 size-6 text-accent/25" />
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} className={`size-4 ${k < r.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />
                  ))}
                </div>
                <p className="text-[15px] leading-relaxed mb-6 text-foreground/90 line-clamp-5">"{r.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <img
                    src={r.photo || initialsAvatar(r.name)}
                    alt=""
                    className="size-11 rounded-full object-cover ring-2 ring-background"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px]">{r.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <Flag country={r.country} size={14} />
                      <span className="truncate">{r.country}{r.role ? ` · ${r.role}` : ""}</span>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
